import {
  NumberForgeState,
  type NumberForgeChargeResult,
  type NumberForgeMoveResult,
  type NumberForgeSnapshot,
  type NumberForgeSource,
} from './NumberForgeState.js';
import type { NumberForgeConfig, NumberForgePlace } from './NumberForgeConfig.js';

export type NumberForgeLayoutMode = 'standard' | 'compact';

export interface NumberForgeVisibleBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export type NumberForgeCommand =
  | { readonly type: 'reveal-source'; readonly source: NumberForgeSource }
  | { readonly type: 'move'; readonly source: NumberForgeSource; readonly place: NumberForgePlace; readonly amount?: number }
  | { readonly type: 'return'; readonly source: NumberForgeSource; readonly place: NumberForgePlace; readonly amount?: number }
  | { readonly type: 'regroup'; readonly place: 'ones' | 'tens' }
  | { readonly type: 'charge' }
  | { readonly type: 'hint' }
  | { readonly type: 'reset' };

export interface NumberForgeChallengeCallbacks {
  readonly onStateChanged?: (snapshot: NumberForgeSnapshot) => void;
  readonly onFeedback?: (result: NumberForgeChargeResult, snapshot: NumberForgeSnapshot) => void;
  readonly onComplete?: (config: NumberForgeConfig, snapshot: NumberForgeSnapshot) => void;
}

/**
 * Rendering-independent Number Forge controller. Pointer, touch, and keyboard
 * views dispatch the same commands, so responsive relayout never resets math state.
 */
export class NumberForgeChallenge {
  readonly state: NumberForgeState;
  private completionPublished = false;
  private layoutMode: NumberForgeLayoutMode = 'standard';
  private suspended = false;

  constructor(
    readonly config: NumberForgeConfig,
    private readonly callbacks: NumberForgeChallengeCallbacks = {},
  ) {
    this.state = new NumberForgeState(config);
  }

  dispatch(command: NumberForgeCommand): NumberForgeMoveResult | NumberForgeChargeResult | void {
    if (this.suspended && command.type !== 'reset') return;

    switch (command.type) {
      case 'reveal-source':
        return this.publishMove(this.state.revealSource(command.source));
      case 'move':
        return this.publishMove(this.state.moveToChannel(command.source, command.place, command.amount));
      case 'return':
        return this.publishMove(this.state.returnToSource(command.source, command.place, command.amount));
      case 'regroup':
        return this.publishMove(this.state.regroup(command.place));
      case 'charge':
        return this.charge();
      case 'hint':
        this.state.requestHint();
        this.publish();
        return;
      case 'reset':
        this.completionPublished = false;
        this.state.reset();
        this.publish();
        return;
    }
  }

  charge() {
    const result = this.state.charge();
    const snapshot = this.state.snapshot;
    if (result !== 'complete') this.callbacks.onFeedback?.(result, snapshot);
    this.publish();
    return result;
  }

  layout(bounds: NumberForgeVisibleBounds) {
    const visibleWidth = bounds.right - bounds.left;
    const visibleHeight = bounds.bottom - bounds.top;
    this.layoutMode = visibleWidth < 1100 || visibleHeight < 640 ? 'compact' : 'standard';
    return this.layoutMode;
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  get currentLayoutMode() { return this.layoutMode; }
  get snapshot() { return this.state.snapshot; }

  private publishMove(result: NumberForgeMoveResult) {
    if (result === 'accepted' || result === 'ready' || result === 'complete') this.publish();
    return result;
  }

  private publish() {
    const snapshot = this.state.snapshot;
    this.callbacks.onStateChanged?.(snapshot);
    if (snapshot.isComplete && !this.completionPublished) {
      this.completionPublished = true;
      this.callbacks.onComplete?.(this.config, snapshot);
    }
  }
}
